import React, { memo, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';

import Charts from '@components/Kpi/Charts';
import CompareSection from '@components/Kpi/CompareSection';
import ParametersOnMap from '@components/Kpi/ParametersOnMap';
import MapOverlayCard from '@components/Map/MapOverlayCard';
import EmptyStubKpi from '@components/Map/SitesInfo/EmptyStubKpi';
import MultipleSitesInfo from '@components/Map/SitesInfo/MultipleSitesInfo';
import SingleSiteInfo from '@components/Map/SitesInfo/SingleSiteInfo';

import { useKPI } from '@hooks/kpis/useKPI';

import { auth, kpi, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	clearCompareCharts,
	clearCorrelateCharts,
	setCompareCharts,
	setCorrelateCharts,
	setSiteIdsOfActiveCharts,
} from '@slices/kpi';
import { setRightSideControlsByFlag } from '@slices/map';

import { isEmpty } from '@utils/common';

import { ISiteProfile } from '@interfaces/cells/siteProfile';
import { IKpiChartsDTO } from '@interfaces/kpi/dto/kpiDTO';
import {
	IChart,
	IKPISite,
	ISelectedOnMapSitesByVendor,
} from '@interfaces/kpi/kpiCommon';
import { ICoords } from '@interfaces/map/map';
import { IGroupSelectOptions } from '@components/common/GroupSelectKpi/GroupSelect.types';

import { EControlsType } from '@enums/common';
import { EMapOverlayCardPositions } from '@enums/map';

import { defaultSelectedSitesOnMap } from './constants';

import styles from './DashboardKpiMap.module.scss';

export interface IDashboardKpiProps {
	handleCloseSiteCard: () => void;
	handleHideSiteCard: () => void;
	isSingleSiteSelected: boolean;
	isMultipleSitesSelected: boolean;
	isVisibleSiteInfo: boolean;
	activeSites: ISiteProfile[];
	panTo: (coords: ICoords, zoomLevel: number) => void;
	activeCustomPolygonIds: string[];
}

const DashboardKpiMap = ({
	activeCustomPolygonIds = [],
	activeSites = [],
	handleCloseSiteCard,
	handleHideSiteCard,
	isMultipleSitesSelected,
	isSingleSiteSelected,
	isVisibleSiteInfo,
	panTo,
}: IDashboardKpiProps) => {
	const dispatch = useAppDispatch();

	const [vendor, setVendor] = useState<string>('');

	const { accessPermission } = useAppSelector(auth);

	const {
		activeSiteMarkers,
		rightSideControls: controlsState,
		showMarkerFullInfo,
		siteIdsWithinActivePolygons,
		selectedVendorOnSitesCard,
	} = useAppSelector(map);

	const {
		compareCharts,
		compareSettings,
		correlateCharts,
		correlateSettings,
	} = useAppSelector(kpi);

	useEffect(() => {
		if (selectedVendorOnSitesCard?.name) {
			setVendor(selectedVendorOnSitesCard.name);
		}
	}, [selectedVendorOnSitesCard?.name]);

	const allSelectedSitesOnMap = useMemo((): Array<ISelectedOnMapSitesByVendor> => {
		return activeSites.reduce<Array<ISelectedOnMapSitesByVendor>>((acc, site) => {
			const siteSites: IKPISite[] = site.technologies.map(({ name }) => ({
				siteId: site.siteId as string,
				technology: name,
			} as IKPISite));

			return acc.map((selectedSite) => {
				if (selectedSite.vendor === site.vendor.name) {
					return ({
						...selectedSite,
						sites: [...selectedSite.sites, ...siteSites],
					});
				}

				return selectedSite;
			});
		}, defaultSelectedSitesOnMap);
	}, [activeSites]);

	const {
		networkOperatorKpiUpdater,
		networkOperatorKpiViewer,
		assetConsumerOrganizationAdministrator,
	} = accessPermission;

	const isKpiView = (
		networkOperatorKpiUpdater
		|| networkOperatorKpiViewer
		|| assetConsumerOrganizationAdministrator
	);
	const activeSite = activeSiteMarkers.length && !isEmpty(activeSites) ? activeSites[0] : null;

	const {
		charts,
		compareDrillDownCharts,
		correlation,
		deletePreset,
		drillDownCharts,
		errorTextKpi,
		exportCharts,
		exportChartsFromParams,
		exportCompareCharts,
		exportCorrelateCharts,
		getCharts,
		getCorrelation,
		getDrillDownCharts,
		getDrillDownCompareCharts,
		handleCheckCreatePreset,
		handleCheckUpdatePreset,
		handleUpdatePreset,
		isLoadingCharts,
		isLoadingCorrelation,
		isLoadingDrillDownCharts,
		isLoadingDrillDownCompareCharts,
		isLoadingOptions,
		isLoadingPresetForm,
		options4GFddLimenet,
		options4GFddNokia,
		options4GTddLimenet,
		options4GTddNokia,
		options5GLimenet,
		options5GNokia,
		options5GOran,
		optionsLimenetKpi,
		optionsNokiaKpi,
		optionsVendorsKpi,
		presets,
		progress,
		runProgress,
		setDefaultKpiVendor,
	} = useKPI({
		isKpiView,
		isOranVendor: false,
	});

	const addChartToCompare = (chart: IChart) => dispatch(setCompareCharts({ chart }));

	const clearAllCompareCharts = () => dispatch(clearCompareCharts());

	const addChartToCorrelate = (chart: IChart) => dispatch(setCorrelateCharts({ chart }));

	const clearAllCorrelateCharts = () => dispatch(clearCorrelateCharts());

	const getChartsHandler = (values: IKpiChartsDTO) => {
		getCharts(values);
		dispatch(setRightSideControlsByFlag({
			fieldName: EControlsType.dashboardKpi,
			isOpen: true,
		}));

		dispatch(setSiteIdsOfActiveCharts([...activeSiteMarkers, ...siteIdsWithinActivePolygons]));
	};

	return (
		<>
			{controlsState[EControlsType.dashboardKpi] && isKpiView
				&& <MapOverlayCard
					handleClose={handleCloseSiteCard}
					position={EMapOverlayCardPositions.left}
					right={20}
					top={65}
					width={400}
					withoutPadding
				>
					<div className={styles.dashboardKpiChartsMap}>
						<Charts
							addChartToCompare={addChartToCompare}
							addChartToCorrelate={addChartToCorrelate}
							charts={charts}
							compareCharts={compareCharts}
							compareSettings={compareSettings}
							correlateCharts={correlateCharts}
							correlateSettings={correlateSettings}
							drillDownCharts={drillDownCharts}
							errorTextKpi={errorTextKpi}
							exportCharts={exportCharts}
							getDrillDownCharts={getDrillDownCharts}
							isLoadingCharts={isLoadingCharts}
							isLoadingDrillDownCharts={isLoadingDrillDownCharts}
							isMap
							panTo={panTo}
							progress={progress}
							vendor={vendor}
						/>
					</div>
				</MapOverlayCard>
			}
			{((isVisibleSiteInfo && showMarkerFullInfo) || !!activeCustomPolygonIds.length)
				&& <MapOverlayCard
					handleClose={handleCloseSiteCard}
					handleHide={handleHideSiteCard}
					left={12}
					position={EMapOverlayCardPositions.left}
					top={65}
					width={460}
				>
					{isSingleSiteSelected && !!activeSite
						? <SingleSiteInfo
							panTo={panTo}
							parametersOnMapComponent={<ParametersOnMap
								allSelectedSitesOnMap={allSelectedSitesOnMap}
								createPreset={handleCheckCreatePreset}
								deletePreset={deletePreset}
								exportChartsFromParams={exportChartsFromParams}
								getCharts={getChartsHandler}
								isLoadingCharts={isLoadingCharts}
								isLoadingOptions={isLoadingOptions}
								isLoadingPresetForm={isLoadingPresetForm}
								options4GFddLimenet={options4GFddLimenet}
								options4GFddNokia={options4GFddNokia}
								options4GTddLimenet={options4GTddLimenet}
								options4GTddNokia={options4GTddNokia}
								options5GLimenet={options5GLimenet}
								options5GNokia={options5GNokia}
								options5GOran={options5GOran}
								optionsLimenetKpi={optionsLimenetKpi as IGroupSelectOptions[]}
								optionsNokiaKpi={optionsNokiaKpi as IGroupSelectOptions[]}
								optionsVendorsKpi={optionsVendorsKpi}
								presets={presets}
								runProgress={runProgress}
								setDefaultKpiVendor={setDefaultKpiVendor}
								setVendor={setVendor}
								updatePreset={handleUpdatePreset}
								updatePresetName={handleCheckUpdatePreset}
								vendor={vendor}
							/>}
							site={activeSite}
						/>
						: ((isMultipleSitesSelected && activeSites.length) || !!activeCustomPolygonIds.length)
						&& <MultipleSitesInfo
							parametersOnMapComponent={<ParametersOnMap
								allSelectedSitesOnMap={allSelectedSitesOnMap}
								createPreset={handleCheckCreatePreset}
								deletePreset={deletePreset}
								getCharts={getChartsHandler}
								isLoadingCharts={isLoadingCharts}
								isLoadingOptions={isLoadingOptions}
								isLoadingPresetForm={isLoadingPresetForm}
								options4GFddLimenet={options4GFddLimenet}
								options4GFddNokia={options4GFddNokia}
								options4GTddLimenet={options4GTddLimenet}
								options4GTddNokia={options4GTddNokia}
								options5GLimenet={options5GLimenet}
								options5GNokia={options5GNokia}
								options5GOran={options5GOran}
								optionsLimenetKpi={optionsLimenetKpi as IGroupSelectOptions[]}
								optionsNokiaKpi={optionsNokiaKpi as IGroupSelectOptions[]}
								optionsVendorsKpi={optionsVendorsKpi}
								presets={presets}
								runProgress={runProgress}
								setDefaultKpiVendor={setDefaultKpiVendor}
								setVendor={setVendor}
								updatePreset={handleUpdatePreset}
								updatePresetName={handleCheckUpdatePreset}
								vendor={vendor}
							/>}
						/>
					}
					{!isSingleSiteSelected && !isMultipleSitesSelected && <EmptyStubKpi />}
				</MapOverlayCard>
			}
			<CompareSection
				classNameCompareSection={cn(styles.compareSection, {
					[styles.oneButton]: compareCharts.length || correlateCharts.length,
					[styles.twoButtons]: compareCharts.length && correlateCharts.length,
				})}
				clearCompareCharts={clearAllCompareCharts}
				clearCorrelateCharts={clearAllCorrelateCharts}
				compareCharts={compareCharts}
				compareDrillDownCharts={compareDrillDownCharts}
				compareSettings={compareSettings}
				correlateCharts={correlateCharts}
				correlation={correlation}
				exportCompareCharts={exportCompareCharts}
				exportCorrelateCharts={exportCorrelateCharts}
				getCorrelation={getCorrelation}
				getDrillDownCompareCharts={getDrillDownCompareCharts}
				isLoadingCorrelation={isLoadingCorrelation}
				isLoadingDrillDownCompareCharts={isLoadingDrillDownCompareCharts}
			/>
		</>
	);
};

export default memo(DashboardKpiMap);
